import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Switch,
  FormControlLabel,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Icons
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';
import SaveIcon from '@mui/icons-material/Save';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
}));

const GreenButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#006938',
  color: 'white',
  '&:hover': {
    backgroundColor: '#004d2b',
  },
  borderRadius: theme.spacing(0.5),
  textTransform: 'none',
  fontWeight: 600
}));

const UploadBox = styled(Box)(({ theme }) => ({
  border: '2px dashed #006938',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(3),
  textAlign: 'center',
  backgroundColor: 'rgba(0, 105, 56, 0.04)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 105, 56, 0.08)',
    borderColor: '#004d2b'
  }
}));

const AdminParts = () => {
  const [parts, setParts] = useState([
    {
      id: 1,
      name: 'iPhone 13 OLED Screen',
      category: 'screen',
      brand: 'Apple',
      price: 129.99,
      inStock: true,
      quantity: 25
    },
    {
      id: 2,
      name: 'Samsung Galaxy S21 Battery',
      category: 'battery',
      brand: 'Samsung',
      price: 49.99,
      inStock: true,
      quantity: 42
    },
    {
      id: 3,
      name: 'MacBook Pro Logic Board',
      category: 'motherboard',
      brand: 'Apple',
      price: 299.99,
      inStock: false,
      quantity: 0
    }
  ]);
  
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    brand: '',
    price: '',
    inStock: true,
    quantity: ''
  });
  
  // Categories and brands
  const categories = [
    'screen',
    'battery',
    'motherboard',
    'smartphone',
    'laptop',
    'other'
  ];
  
  const brands = [
    'Apple',
    'Samsung',
    'Dell',
    'HP',
    'Lenovo',
    'Other'
  ];
  
  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    const newPart = {
      id: parts.length + 1,
      ...formData,
      price: parseFloat(formData.price),
      quantity: parseInt(formData.quantity)
    };
    
    setParts([...parts, newPart]);
    
    // Reset form
    setFormData({
      name: '',
      category: '',
      brand: '',
      price: '',
      inStock: true,
      quantity: ''
    });
  };
  
  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 6 }}>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              color: '#006938',
              mb: 2
            }}
          >
            Admin Parts Management
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary"
            sx={{ maxWidth: '800px' }}
          >
            Add, edit, and manage hardware parts in your inventory
          </Typography>
        </Box>
        
        {/* Add New Part Form */}
        <StyledPaper sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#006938' }}>
            Add New Part
          </Typography>
          
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Part Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    label="Category"
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Brand</InputLabel>
                  <Select
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    label="Brand"
                  >
                    {brands.map((brand) => (
                      <MenuItem key={brand} value={brand}>
                        {brand}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Price ($)"
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Quantity"
                  name="quantity"
                  type="number"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  required
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.inStock}
                      onChange={handleInputChange}
                      name="inStock"
                      color="primary"
                    />
                  }
                  label="In Stock"
                />
              </Grid>
              <Grid item xs={12}>
                <UploadBox>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="part-image-upload"
                    type="file"
                  />
                  <label htmlFor="part-image-upload">
                    <Button
                      component="span"
                      startIcon={<UploadIcon />}
                      sx={{ color: '#006938' }}
                    >
                      Upload Part Image
                    </Button>
                  </label>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Drag and drop an image or click to browse
                  </Typography>
                </UploadBox>
              </Grid>
              <Grid item xs={12}>
                <GreenButton
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                >
                  Add Part
                </GreenButton>
              </Grid>
            </Grid>
          </form>
        </StyledPaper>
        
        {/* Parts Table */}
        <StyledPaper>
          <Typography variant="h5" gutterBottom sx={{ color: '#006938' }}>
            Inventory
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parts.map((part) => (
                  <TableRow key={part.id}>
                    <TableCell>{part.id}</TableCell>
                    <TableCell>{part.name}</TableCell>
                    <TableCell>{part.category}</TableCell>
                    <TableCell>{part.brand}</TableCell>
                    <TableCell>${part.price.toFixed(2)}</TableCell>
                    <TableCell>{part.quantity}</TableCell>
                    <TableCell>
                      {part.inStock ? (
                        <Alert severity="success" sx={{ py: 0 }}>In Stock</Alert>
                      ) : (
                        <Alert severity="error" sx={{ py: 0 }}>Out of Stock</Alert>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton color="primary" size="small">
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" size="small">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </StyledPaper>
      </Container>
    </Box>
  );
};

export default AdminParts; 